import React, { useState } from 'react';
import { Card, Space, Typography, Table, Button, Select, Tag, message } from 'antd';
import { DownloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetChargebacksQuery,
    useGetPanelStatsQuery,
} from '../../../services/nestjs-base-api';

const { Title, Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
    prepared: 'gold',
    submitted: 'blue',
    accepted: 'green',
    partially_accepted: 'cyan',
    disputed: 'orange',
    replaced: 'purple',
    withdrawn: 'default',
};

interface ChargebackRow {
    id: number;
    panelPartner: string;
    panelPartnerDisplayName: string | null;
    surveyId: number;
    surveyName: string | null;
    brandName: string | null;
    responseCount: number;
    status: string;
    submittedAt: string | null;
    resolvedAt: string | null;
    creditAmountUsd: number | null;
    createdAt: string;
}

interface PanelStatsRow {
    panelPartner: string;
    displayName: string | null;
    totalResponses: number;
    rejectedResponses: number;
    rejectionRate: number;
    preparedChargebacks: number;
    submittedChargebacks: number;
    acceptedChargebacks: number;
    totalCreditUsd: number;
}

export const ChargebackListPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();

    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [panelFilter, setPanelFilter] = useState<string | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);

    const { data, isLoading, isFetching } = useGetChargebacksQuery({
        jwtToken: user?.jwtToken || '',
        status: statusFilter,
        panelPartner: panelFilter,
        page,
        limit,
    });
    const { data: stats } = useGetPanelStatsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const rows: ChargebackRow[] = data?.data ?? [];
    const total: number = data?.total ?? 0;
    const statsRows: PanelStatsRow[] = stats?.rows ?? [];

    const handleDownload = async (id: number) => {
        try {
            const res = await fetch(
                `http://172.25.48.1:3501/admin/response-integrity/chargebacks/${id}/evidence`,
                {
                    headers: {
                        Authorization: `Bearer ${user?.jwtToken || ''}`,
                    },
                },
            );
            if (!res.ok) throw new Error(String(res.status));
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download =
                res.headers
                    .get('Content-Disposition')
                    ?.match(/filename="?([^";]+)/i)?.[1] ?? `chargeback-${id}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(
                intl.formatMessage({
                    id: 'responseIntegrity.chargebacks.evidenceDownloadFailed',
                }),
            );
        }
    };

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="responseIntegrity.chargebacks.title" />
                    </Title>
                    <Text type="secondary">
                        <FormattedMessage id="responseIntegrity.chargebacks.subtitle" />
                    </Text>
                </div>

                {statsRows.length > 0 && (
                    <Card title={<FormattedMessage id="responseIntegrity.panelStats.title" />}>
                        <Table<PanelStatsRow>
                            rowKey="panelPartner"
                            dataSource={statsRows}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.partner" />,
                                    dataIndex: 'displayName',
                                    render: (_v, row) =>
                                        row.displayName || row.panelPartner,
                                },
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.totalResponses" />,
                                    dataIndex: 'totalResponses',
                                    align: 'right',
                                },
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.rejectedResponses" />,
                                    dataIndex: 'rejectedResponses',
                                    align: 'right',
                                },
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.rejectionRate" />,
                                    dataIndex: 'rejectionRate',
                                    align: 'right',
                                    render: (v: number) =>
                                        `${(v * 100).toFixed(1)}%`,
                                },
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.preparedChargebacks" />,
                                    dataIndex: 'preparedChargebacks',
                                    align: 'right',
                                },
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.submittedChargebacks" />,
                                    dataIndex: 'submittedChargebacks',
                                    align: 'right',
                                },
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.acceptedChargebacks" />,
                                    dataIndex: 'acceptedChargebacks',
                                    align: 'right',
                                },
                                {
                                    title: <FormattedMessage id="responseIntegrity.panelStats.col.totalCredit" />,
                                    dataIndex: 'totalCreditUsd',
                                    align: 'right',
                                    render: (v: number) =>
                                        v.toFixed(2),
                                },
                            ]}
                        />
                    </Card>
                )}

                <Card>
                    <Space>
                        <Select
                            allowClear
                            style={{ width: 220 }}
                            placeholder="Status"
                            value={statusFilter}
                            onChange={(v) => {
                                setStatusFilter(v);
                                setPage(1);
                            }}
                            options={Object.keys(STATUS_COLORS).map((s) => ({
                                value: s,
                                label: intl.formatMessage({
                                    id: `responseIntegrity.chargebacks.status.${s}`,
                                }),
                            }))}
                        />
                        <Select
                            allowClear
                            style={{ width: 220 }}
                            placeholder="Panel partner"
                            value={panelFilter}
                            onChange={(v) => {
                                setPanelFilter(v);
                                setPage(1);
                            }}
                            options={statsRows.map((r) => ({
                                value: r.panelPartner,
                                label: r.displayName || r.panelPartner,
                            }))}
                        />
                    </Space>
                </Card>

                <Card styles={{ body: { padding: 0 } }}>
                    <Table<ChargebackRow>
                        rowKey="id"
                        loading={isLoading || isFetching}
                        dataSource={rows}
                        pagination={{
                            current: page,
                            pageSize: limit,
                            total,
                            onChange: (p, ps) => {
                                setPage(p);
                                setLimit(ps);
                            },
                        }}
                        locale={{
                            emptyText: intl.formatMessage({
                                id: 'responseIntegrity.chargebacks.empty',
                            }),
                        }}
                        columns={[
                            {
                                title: <FormattedMessage id="responseIntegrity.chargebacks.col.panelPartner" />,
                                dataIndex: 'panelPartnerDisplayName',
                                render: (_v, row) =>
                                    row.panelPartnerDisplayName || row.panelPartner,
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.chargebacks.col.survey" />,
                                dataIndex: 'surveyName',
                                render: (name: string, row) => (
                                    <div>
                                        <div>{name || `Survey #${row.surveyId}`}</div>
                                        {row.brandName && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {row.brandName}
                                            </Text>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.chargebacks.col.responseCount" />,
                                dataIndex: 'responseCount',
                                align: 'right',
                                width: 120,
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.chargebacks.col.status" />,
                                dataIndex: 'status',
                                width: 160,
                                render: (s: string) => (
                                    <Tag color={STATUS_COLORS[s] ?? 'default'} style={{ margin: 0 }}>
                                        <FormattedMessage
                                            id={`responseIntegrity.chargebacks.status.${s}`}
                                        />
                                    </Tag>
                                ),
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.chargebacks.col.submitted" />,
                                dataIndex: 'submittedAt',
                                width: 140,
                                render: (v: string | null) =>
                                    v ? dayjs(v).format('YYYY-MM-DD') : '—',
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.chargebacks.col.credit" />,
                                dataIndex: 'creditAmountUsd',
                                align: 'right',
                                width: 120,
                                render: (v: number | null) =>
                                    v === null || v === undefined ? '—' : v.toFixed(2),
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.chargebacks.col.actions" />,
                                key: 'actions',
                                width: 220,
                                render: (_v, row) => (
                                    <Space>
                                        <Link to={`/admin/response-integrity/chargebacks/${row.id}`}>
                                            <Button size="small">
                                                <FormattedMessage id="responseIntegrity.chargebacks.action.view" />
                                            </Button>
                                        </Link>
                                        <Button
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            onClick={() => handleDownload(row.id)}
                                        >
                                            <FormattedMessage id="responseIntegrity.chargebacks.action.download" />
                                        </Button>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                </Card>
            </Space>
        </div>
    );
};
